/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo, useState, useEffect } from 'react';
import { CandlestickData, ChartTimeframe } from '../types/charts';
import { Loader2, BarChart2, TrendingUp, TrendingDown } from 'lucide-react';
import { getBirdeyeHistory } from '../lib/solana/birdeye';
import { bondingCurveDB } from '../lib/bonding-curve-db';

interface TradingChartProps {
    symbol: string;
    basePrice: number;
    timeframe: ChartTimeframe;
    onTimeframeChange: (tf: ChartTimeframe) => void;
    tokenMint?: string;
    launchDate?: number; // timestamp
}

export const TradingChart: React.FC<TradingChartProps> = ({ 
    symbol, 
    basePrice, 
    timeframe, 
    onTimeframeChange,
    tokenMint,
    launchDate
}) => {
    const [data, setData] = useState<CandlestickData[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            setLoading(true);
            try {
                // 1. Try real external data
                // Mock address derivation for the demo data fetcher
                const address = symbol === 'SOL' ? 'So11111111111111111111111111111111111111112' : (tokenMint || `mock-${symbol}`);
                const history = await getBirdeyeHistory(address, timeframe);
                
                if (mounted && history.length > 0) {
                    const mappedData: CandlestickData[] = history.map(h => ({
                        time: h.time,
                        open: h.o,
                        high: h.h,
                        low: h.l,
                        close: h.c,
                        volume: h.v
                    }));
                    setData(mappedData);
                } else if (mounted) {
                    // 2. Fallback: Generate "Since Launch" Bonding Curve Chart
                    // This creates a realistic chart for new tokens that APIs haven't indexed yet
                    const now = Math.floor(Date.now() / 1000);
                    const created = Math.floor((launchDate || (Date.now() - 3600000)) / 1000); // Default 1 hour ago
                    const duration = now - created;
                    const interval = timeframe === '15m' ? 900 : timeframe === '1H' ? 3600 : 14400;
                    
                    const candlesCount = Math.max(5, Math.ceil(duration / interval));
                    const generated: CandlestickData[] = [];
                    
                    // Bonding curve logic: Price increases with supply (simplified)
                    let currentP = basePrice * 0.5; // Start lower than current
                    
                    for (let i = 0; i < candlesCount; i++) {
                        const time = created + (i * interval);
                        
                        // Simulate upward trend of a bonding curve
                        const trend = (i / candlesCount) * 0.8; // Upward bias
                        const volatility = currentP * 0.05; 
                        
                        const open = currentP;
                        const change = (Math.random() * volatility) + (volatility * 0.2); // Mostly up
                        const close = Math.max(0.000001, open + change);
                        
                        const high = Math.max(open, close) * 1.02;
                        const low = Math.min(open, close) * 0.98;
                        const volume = Math.random() * 10000;

                        generated.push({ time, open, high, low, close, volume });
                        currentP = close;
                    }
                    
                    // Force last candle to match current price
                    if (generated.length > 0) {
                        generated[generated.length - 1].close = basePrice;
                        generated[generated.length - 1].high = Math.max(generated[generated.length - 1].high, basePrice);
                    }

                    setData(generated);
                }
            } catch (e) {
                console.error("Failed to load chart data", e);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchData();
        return () => { mounted = false; };
    }, [symbol, timeframe, tokenMint, launchDate]);

    // Calculate chart scaling
    const { maxPrice, minPrice, priceRange } = useMemo(() => {
        if (data.length === 0) return { maxPrice: basePrice * 1.1, minPrice: basePrice * 0.9, priceRange: basePrice * 0.2 };
        const highs = data.map(d => d.high);
        const lows = data.map(d => d.low);
        const max = Math.max(...highs);
        const min = Math.min(...lows);
        const range = max - min;
        return { maxPrice: max, minPrice: min, priceRange: range === 0 ? max * 0.1 : range };
    }, [data, basePrice]);

    const formatPrice = (p: number) => {
        if (p < 0.0001) return p.toExponential(2);
        if (p < 1) return p.toFixed(4);
        return p.toFixed(2);
    };

    return (
        <div className="flex flex-col h-full w-full bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-stone-100 rounded-lg">
                        <BarChart2 size={20} className="text-stone-600" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-bold text-stone-900">{symbol}/USD</h3>
                            <span className="text-xs font-mono text-stone-400">Price Action</span>
                        </div>
                        <div className="text-xs font-bold text-stone-500">
                            {loading ? 'Syncing...' : `Last: $${formatPrice(data[data.length - 1]?.close || basePrice)}`}
                        </div>
                    </div>
                </div>

                <div className="flex bg-stone-100 p-1 rounded-lg">
                    {(['15m', '1H', '4H', '1D'] as ChartTimeframe[]).map((tf) => (
                        <button
                            key={tf}
                            onClick={() => onTimeframeChange(tf)}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                                timeframe === tf 
                                ? 'bg-white text-stone-900 shadow-sm' 
                                : 'text-stone-500 hover:text-stone-900 hover:bg-stone-200'
                            }`}
                        >
                            {tf}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex-1 relative min-h-[300px] w-full bg-[#F9F8F4] rounded-lg border border-stone-100 overflow-hidden cursor-crosshair group">
                {loading && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/50 backdrop-blur-sm">
                        <Loader2 className="animate-spin text-nobel-gold" size={32} />
                    </div>
                )}
                
                {/* Grid Lines */}
                <div className="absolute inset-0 flex flex-col justify-between py-4 px-2 pointer-events-none opacity-20">
                    <div className="w-full h-px bg-stone-300"></div>
                    <div className="w-full h-px bg-stone-300"></div>
                    <div className="w-full h-px bg-stone-300"></div>
                    <div className="w-full h-px bg-stone-300"></div>
                    <div className="w-full h-px bg-stone-300"></div>
                </div>

                {/* Candles */}
                <div className="absolute inset-0 flex items-end justify-between px-4 py-4 gap-1">
                    {data.length > 0 ? data.slice(-40).map((d, i) => {
                        const isGreen = d.close >= d.open;
                        const heightPercent = ((d.high - d.low) / priceRange) * 100;
                        const bottomPercent = ((d.low - minPrice) / priceRange) * 100;
                        const bodyTop = Math.max(d.open, d.close);
                        const bodyBottom = Math.min(d.open, d.close);
                        const bodyHeight = Math.max(((bodyTop - bodyBottom) / priceRange) * 100, 1);
                        const bodyBottomPos = ((bodyBottom - minPrice) / priceRange) * 100;

                        return (
                            <div key={i} className="flex-1 h-full relative group/candle flex flex-col items-center">
                                {/* Wick */}
                                <div 
                                    className={`w-[1px] absolute ${isGreen ? 'bg-green-500' : 'bg-red-500'}`}
                                    style={{ height: `${heightPercent}%`, bottom: `${bottomPercent}%` }}
                                ></div>
                                {/* Body */}
                                <div 
                                    className={`w-full max-w-[8px] absolute rounded-[1px] ${isGreen ? 'bg-green-500' : 'bg-red-500'}`}
                                    style={{ height: `${bodyHeight}%`, bottom: `${bodyBottomPos}%` }}
                                ></div>
                                
                                {/* Hover Info */}
                                <div className="hidden group-hover/candle:block absolute bottom-full mb-2 z-30 bg-stone-900 text-white text-[10px] p-2 rounded shadow-lg whitespace-nowrap pointer-events-none">
                                    <div className="font-bold mb-1">{new Date(d.time * 1000).toLocaleTimeString()}</div>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                                        <span className="text-stone-400">O:</span> <span>{d.open.toFixed(4)}</span>
                                        <span className="text-stone-400">H:</span> <span>{d.high.toFixed(4)}</span>
                                        <span className="text-stone-400">L:</span> <span>{d.low.toFixed(4)}</span>
                                        <span className="text-stone-400">C:</span> <span>{d.close.toFixed(4)}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    }) : (
                        <div className="w-full h-full flex items-center justify-center text-stone-400 text-sm">
                            No chart data available for this pair.
                        </div>
                    )}
                </div>
                
                {/* Price Axis Labels (Right Side) */}
                <div className="absolute right-0 top-0 bottom-0 py-4 flex flex-col justify-between text-[9px] font-mono text-stone-400 pr-1 pointer-events-none bg-gradient-to-l from-white/80 to-transparent pl-4">
                    <span>{formatPrice(maxPrice)}</span>
                    <span>{formatPrice(minPrice + (priceRange * 0.75))}</span>
                    <span>{formatPrice(minPrice + (priceRange * 0.5))}</span>
                    <span>{formatPrice(minPrice + (priceRange * 0.25))}</span>
                    <span>{formatPrice(minPrice)}</span>
                </div>
            </div>
        </div>
    );
};